import cn from 'classnames';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { DateRangeParams } from '@utils/types/resellers/dashboard.ts';
import { Calendar, X } from 'lucide-react';

type DateRangePickerProps = {
  value: DateRangeParams;
  onChange: (range: DateRangeParams) => void;
  className?: string;
};

export default function DateRangePicker({
  value,
  onChange,
  className
}: DateRangePickerProps) {
  const { getColour } = useThemeContext();

  const handleStartChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({
      ...value,
      periodStart: e.target.value || undefined
    });
  };

  const handleEndChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({
      ...value,
      periodEnd: e.target.value || undefined
    });
  };

  const clearDates = () => {
    onChange({});
  };

  return (
    <div
      className={cn(
        'flex items-center justify-between gap-1.5 sm:gap-3 rounded-2xl p-2 w-full sm:w-auto',
        className
      )}
      style={{
        backgroundColor: getColour('baseLayer')
      }}
    >
      <Calendar size={16} className="hidden sm:block flex-shrink-0" />

      <div className="flex items-center gap-1 sm:gap-2 min-w-0">
        <label className="text-xs font-medium whitespace-nowrap">From:</label>
        <input
          type="date"
          value={value.periodStart || ''}
          onChange={handleStartChange}
          className="w-[110px] sm:w-auto rounded-2xl border px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none transition-colors"
          placeholder="Start date"
          style={{
            backgroundColor: getColour('secondLayer'),
            borderColor: getColour('border'),
            color: getColour('normalText')
          }}
        />
      </div>

      <span className="text-xs sm:text-base">—</span>

      <div className="flex items-center gap-1 sm:gap-2 min-w-0">
        <label className="text-xs font-medium whitespace-nowrap">To:</label>
        <input
          type="date"
          value={value.periodEnd || ''}
          onChange={handleEndChange}
          className="w-[110px] sm:w-auto rounded-2xl border px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none transition-colors"
          placeholder="End date"
          style={{
            backgroundColor: getColour('secondLayer'),
            borderColor: getColour('border'),
            color: getColour('normalText')
          }}
        />
      </div>

      {(value.periodStart || value.periodEnd) && (
        <button
          onClick={clearDates}
          className="flex items-center justify-center flex-shrink-0 text-xs text-blue-600 hover:text-blue-800 transition-colors"
          type="button"
          title="Clear dates"
        >
          <X size={14} />
        </button>
      )}
    </div>
  );
}
